import { useEffect, useState } from 'react'
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2, CheckCircle2, Building2, Briefcase } from 'lucide-react'
import api from '@/shared/lib/api'
import { useAuthStore } from '@/core/stores/auth'
import { useNavigate, Link } from 'react-router-dom'

export function SignupPage() {
  const { login, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [step, setStep] = useState<1 | 2>(1)
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isAuthenticated) navigate('/home', { replace: true })
  }, [isAuthenticated, navigate])

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  }
  const passwordValid = passwordChecks.length && passwordChecks.uppercase && passwordChecks.number

  const canProceedStep1 = fullName.trim() && email.trim() && organizationName.trim() && workspaceName.trim()

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canProceedStep1) {
      setError('Veuillez remplir tous les champs.')
      return
    }
    setError('')
    setStep(2)
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!passwordValid) {
      setError('Le mot de passe ne respecte pas les critères requis.')
      return
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.')
      return
    }

    setLoading(true)
    try {
      const { data } = await api.post('/auth/register', {
        full_name: fullName,
        email,
        password,
        organization_name: organizationName,
        workspace_name: workspaceName,
        job_title: jobTitle,
      })
      await login(data.access_token)
      navigate('/home', { replace: true })
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(detail || "Une erreur est survenue lors de l'inscription.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-onyx flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-gold/10 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-teal/10 blur-[100px]" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <svg width="36" height="36" viewBox="0 0 44 44" fill="none">
              <circle cx="22" cy="22" r="8" stroke="#C9A84C" strokeWidth="2" fill="none"/>
              <circle cx="22" cy="22" r="14" stroke="#C9A84C" strokeWidth="1.2" opacity="0.4" fill="none"/>
              <circle cx="22" cy="22" r="20" stroke="#C9A84C" strokeWidth="0.8" opacity="0.2" fill="none"/>
              <circle cx="22" cy="22" r="3" fill="#C9A84C"/>
            </svg>
            <span className="font-serif text-xl font-bold text-paper">TremorApp</span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="font-serif text-4xl font-bold text-paper leading-tight">
            Intelligence marché<br />
            pour agences<br />
            <span className="text-gold">ambitieuses</span>
          </h1>
          <p className="text-paper/50 text-lg max-w-md leading-relaxed">
            Créez votre organisation et commencez à piloter
            vos clients avec des données actionnables.
          </p>
        </div>

        <div className="relative z-10">
          <p className="text-paper/30 text-sm">© 2026 TremorApp — Market Intelligence Platform</p>
        </div>
      </div>

      {/* Right panel — signup form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-paper">
        <div className="w-full max-w-[420px] space-y-8">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden">
            <svg width="32" height="32" viewBox="0 0 44 44" fill="none">
              <circle cx="22" cy="22" r="8" stroke="#C9A84C" strokeWidth="2" fill="none"/>
              <circle cx="22" cy="22" r="14" stroke="#C9A84C" strokeWidth="1.2" opacity="0.4" fill="none"/>
              <circle cx="22" cy="22" r="3" fill="#C9A84C"/>
            </svg>
            <span className="font-serif text-lg font-bold text-ink">TremorApp</span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${step >= 1 ? 'bg-gold text-onyx' : 'bg-paper-warm text-ink-faint'}`}>1</div>
            <div className="flex-1 h-0.5 bg-ink-border rounded">
              <div className={`h-full bg-gold rounded transition-all ${step === 2 ? 'w-full' : 'w-0'}`} />
            </div>
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${step === 2 ? 'bg-gold text-onyx' : 'bg-paper-warm text-ink-faint'}`}>2</div>
          </div>

          <div className="space-y-2">
            <h2 className="font-serif text-2xl font-bold text-ink">
              {step === 1 ? 'Créer votre organisation' : 'Sécurisez votre compte'}
            </h2>
            <p className="text-ink-muted text-sm">
              {step === 1 ? 'Informations sur vous et votre entreprise' : 'Choisissez un mot de passe sécurisé'}
            </p>
          </div>

          {/* Step 1: Identity + Org */}
          {step === 1 && (
            <form onSubmit={handleStep1} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Nom complet</label>
                <div className="relative">
                  <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jean Dupont" autoComplete="name"
                    className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Adresse email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@entreprise.com" autoComplete="email"
                    className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Nom de l'organisation</label>
                <div className="relative">
                  <Building2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input type="text" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Mon Agence Marketing"
                    className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                </div>
                <p className="text-[11px] text-ink-faint">Votre entreprise ou agence. Regroupe vos workspaces.</p>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Nom du workspace</label>
                <div className="relative">
                  <Briefcase size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input type="text" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)}
                    placeholder="Client Premium, Projet X, ..."
                    className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                </div>
                <p className="text-[11px] text-ink-faint">Un workspace = un environnement de travail avec ses propres données.</p>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Votre poste <span className="text-ink-faint font-normal">(optionnel)</span></label>
                <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Directeur Marketing, CEO, ..."
                  className="w-full rounded-lg border border-ink-border bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
              </div>

              <button type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-onyx px-4 py-3 text-sm font-semibold text-paper transition-all hover:bg-onyx-light active:scale-[0.98]">
                Continuer <ArrowRight size={16} />
              </button>
            </form>
          )}

          {/* Step 2: Password */}
          {step === 2 && (
            <form onSubmit={handleSignup} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</div>
              )}

              {/* Recap */}
              <div className="bg-white rounded-lg border border-ink-border p-4 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-faint">Organisation</span>
                  <span className="text-xs font-medium text-ink">{organizationName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-faint">Workspace</span>
                  <span className="text-xs font-medium text-ink">{workspaceName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-faint">Email</span>
                  <span className="text-xs font-medium text-ink">{email}</span>
                </div>
                <button type="button" onClick={() => setStep(1)} className="text-[11px] text-gold-dark hover:underline mt-1">Modifier</button>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Mot de passe</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 caractères" autoComplete="new-password"
                    className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-11 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="space-y-1 pt-1">
                    <PasswordCheck ok={passwordChecks.length} label="8 caractères minimum" />
                    <PasswordCheck ok={passwordChecks.uppercase} label="Une majuscule" />
                    <PasswordCheck ok={passwordChecks.number} label="Un chiffre" />
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">Confirmer le mot de passe</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Retapez votre mot de passe" autoComplete="new-password"
                    className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-ink-muted hover:bg-paper-warm transition-colors">
                  Retour
                </button>
                <button type="submit" disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-onyx px-4 py-3 text-sm font-semibold text-paper transition-all hover:bg-onyx-light active:scale-[0.98] disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <>Créer mon compte <ArrowRight size={16} /></>}
                </button>
              </div>
            </form>
          )}

          <p className="text-center text-sm text-ink-muted">
            Déjà un compte ?{' '}
            <Link to="/login" className="font-medium text-gold-dark hover:underline">Se connecter</Link>
          </p>

          <p className="text-center text-xs text-ink-faint">
            En créant un compte, vous acceptez nos <Link to="/cgu" className="underline">CGU</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

function PasswordCheck({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <CheckCircle2 size={14} className={ok ? 'text-emerald-500' : 'text-ink-border'} />
      <span className={ok ? 'text-emerald-600' : 'text-ink-faint'}>{label}</span>
    </div>
  )
}
